// ---------------------------------------------------------------------------
// Derivação do progresso por item da ordem (refatoração visual 2026-09-10).
//
// A barra empilhada divide o PEDIDO de cada item em quatro parcelas:
//
//    conferido    = item.received_qtd            (entrou no destino)
//    falta        = Σ divergent_qtd              nas remessas RECEIVED |
//                                                RECEIVED_WITH_DIVERGENCE
//    em trânsito  = Σ shipped_qtd                nas remessas DISPATCHED
//    a expedir    = item.remaining_qtd           (trilho — acumulador do
//                                                backend, nunca derivado aqui)
//
// ⚠️ As duas armadilhas que o protótipo errou (e que o teste trava):
//    1. `shipped − received` NÃO é "em trânsito": numa remessa já conferida
//       essa diferença é PERDA. Em trânsito só existe em remessa DISPATCHED.
//    2. Divergência só existe DEPOIS da conferência — linha de remessa
//       DISPATCHED não gera falta, badge nem tratativa.
//
// Remessa CANCELLED fica fora de todas as parcelas: o estorno devolveu o
// estoque à origem e os acumuladores da ordem já refletem isso.
// ---------------------------------------------------------------------------

import { SHIPMENT_STATUS } from '../constants';

const RECEIVED_STATUSES = new Set([
   SHIPMENT_STATUS.RECEIVED,
   SHIPMENT_STATUS.RECEIVED_WITH_DIVERGENCE,
]);

function num(value) {
   const parsed = Number(value);
   return Number.isFinite(parsed) ? parsed : 0;
}

export function buildItemProgress(item, shipments = []) {
   const requested = num(item.requested_qtd);
   const shipped = num(item.shipped_qtd);
   const received = num(item.received_qtd);
   const settled = num(item.settled_qtd);
   const remaining = num(item.remaining_qtd);

   let inTransit = 0;
   let missing = 0;
   let missingPending = 0;
   let missingSettled = 0;

   shipments.forEach((shipment) => {
      const line = (shipment.items ?? []).find(
         (l) => String(l.prod_id) === String(item.prod_id)
      );
      if (!line) return;

      if (shipment.status === SHIPMENT_STATUS.DISPATCHED) {
         inTransit += num(line.shipped_qtd);
         return;
      }

      if (RECEIVED_STATUSES.has(shipment.status)) {
         const divergent = num(line.divergent_qtd);
         if (divergent <= 0) return;
         missing += divergent;
         if (line.divergence_settled_at) missingSettled += divergent;
         else missingPending += divergent;
      }
      // CANCELLED: fora da conta (estoque devolvido à origem).
   });

   // O total de referência da barra é o pedido; se os acumuladores somarem
   // mais (edição concorrente entre o GET e uma mutação), a barra satura em
   // 100% em vez de estourar o layout.
   const total = Math.max(requested, received + missing + inTransit + remaining);
   const pct = (value) =>
      total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0;

   return {
      requested,
      shipped,
      received,
      settled,
      remaining,
      inTransit,
      missing,
      missingPending,
      missingSettled,
      segments: {
         received: pct(received),
         missing: pct(missing),
         transit: pct(inTransit),
      },
   };
}

// Agregado da seção "Itens do pedido": soma das parcelas de todos os itens,
// com o percentual de conferido sobre o pedido total.
export function buildOrderProgress(items = [], shipments = []) {
   const rows = items.map((item) => buildItemProgress(item, shipments));
   const sum = (key) => rows.reduce((acc, row) => acc + row[key], 0);

   const requested = sum('requested');
   const received = sum('received');
   const missing = sum('missing');
   const inTransit = sum('inTransit');
   const remaining = sum('remaining');

   const total = Math.max(requested, received + missing + inTransit + remaining);
   const pct = (value) =>
      total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0;

   return {
      requested,
      shipped: sum('shipped'),
      received,
      settled: sum('settled'),
      remaining,
      inTransit,
      missing,
      missingPending: sum('missingPending'),
      missingSettled: sum('missingSettled'),
      pctReceived: requested > 0 ? Math.round((received / requested) * 100) : 0,
      segments: {
         received: pct(received),
         missing: pct(missing),
         transit: pct(inTransit),
      },
   };
}
